import { ElementRef, Injectable, Renderer2 } from '@angular/core';
import { MathJaxConfigService } from '../latex/mathjax-config.service';

@Injectable({
  providedIn: 'root',
})
export class EquationService {
  public SABISEquationElementName: string = 'sabis-equation';

  constructor(private _mathjaxConfService: MathJaxConfigService) {
  }

  public async insertEquationFromImageHtml(div: ElementRef<HTMLTextAreaElement>, imgHtml: string): Promise<void> {
    let wrapperSpan = await this.createEquationFromHtml(imgHtml);
    if (!wrapperSpan) {
      return;
    }

    this.insertEquationAtCaret(div, wrapperSpan);
  }

  /** Insert an equation at the current caret position in the editable div */
  private insertEquationAtCaret(div: ElementRef<HTMLTextAreaElement>, element: HTMLElement): void {
    // Focus the div first
    div.nativeElement.focus();

    // Insert HTML at caret
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel && sel.getRangeAt && sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(element);

        sel.removeAllRanges();
        sel.addRange(range);
        range.collapse(false);
      }
    }
  }

  public async replaceImage(renderer: Renderer2, div: ElementRef<HTMLTextAreaElement>, targetSABISEquationElement: Element,
    newImage: string): Promise<void> {

    let newWrapperElement = await this.createEquationFromHtml(newImage);
    if (!newWrapperElement) {
      console.error('Failed to create new equation element');
      return;
    }

    const parentSpan = targetSABISEquationElement.parentElement;
    renderer.insertBefore(parentSpan, newWrapperElement, targetSABISEquationElement);
    renderer.removeChild(parentSpan, targetSABISEquationElement);
  }

  /**
   * Creates an equation element from HTML string using Renderer2
   * @param imgHtml - HTML string containing img element
   * @returns Created equation element
   */
  private async createEquationFromHtml(imgHtml: string): Promise<HTMLElement | null> {
    // create div to query it's element
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = imgHtml;
    const img = tempDiv.querySelector('img');

    if (!img) {
      return null;
    }

    const mathml = img.getAttribute('data-mathml');
    if (!mathml) {
      return null;
    }

    const sanitizedMathML = this.sanitizeMathML(mathml);

    // creat wrapper sabis-equation-wrapper (it will contain all new elements)
    // Warning: modifying wrapper element will require backend changes as well, since we are using it to split messages
    const wrapperSpan = document.createElement('sabis-equation-wrapper');
    wrapperSpan.setAttribute('contenteditable', 'false');
    wrapperSpan.style.display = 'inline-block';

    // create sabis equation element
    const equationElement = document.createElement(this.SABISEquationElementName);
    equationElement.setAttribute('contenteditable', 'false');
    equationElement.setAttribute('data-mathml', sanitizedMathML);
    wrapperSpan.appendChild(equationElement);

    // Insert zero-width space to maintain correct cursor position.
    // Fixes bug # 400777: Text added after an equation incorrectly jumps before it due to predictive text.
    const zeroWidthSpace = document.createTextNode('\u200B');
    wrapperSpan.appendChild(zeroWidthSpace);

    // set the innner html to the mathml so it will be rendered as an equation (using mathjax svg)
    equationElement.innerHTML = sanitizedMathML;

    // Clear the typeset
    this._mathjaxConfService.typesetClear(equationElement).then(x => {
      // convert the innerHtml into mathjax (svg)
      this._mathjaxConfService.typeset(equationElement);
    });

    return wrapperSpan;
  }

  public sanitizeMathML(raw: string): string {
    // 1. Normalize strange characters
    let normalized = raw
      .replace(/«/g, '<')
      .replace(/»/g, '>')
      .replace(/¨/g, '"')
      .replace(/§/g, '&');

    // 2. Parse as XML
    const parser = new DOMParser();
    const doc = parser.parseFromString(normalized, 'application/xml');

    // 3. Remove unwanted attributes
    const unwantedAttrs = [
      'data-latex',
      'data-latex-item',
      'data-mjx-texclass',
      'columnspacing',
      'rowspacing',
      'columnalign',
      'data-frame-styles',
      'framespacing',
      'fence',
      'stretchy',
      'symmetric'
    ];

    doc.querySelectorAll('*').forEach(el => {
      unwantedAttrs.forEach(attr => el.removeAttribute(attr));
    });

    // 4. Remove empty menclose inside mrow
    doc.querySelectorAll('menclose').forEach(menclose => {
      const innerMrow = menclose.querySelector('mrow');

      if (innerMrow && innerMrow.children.length === 0 && innerMrow.textContent?.trim() === '') {
        const parent = menclose.parentElement;
        if (parent) {
          parent.replaceChild(innerMrow, menclose);
        }
      }
    });

    // 5. Serialize back to string
    const serializer = new XMLSerializer();
    return serializer.serializeToString(doc);
  }

  public restoreEquationMathML(container: HTMLElement): void {
    if (!container) return;
    const elements = container.querySelectorAll('sabis-equation[data-mathml]');
    elements.forEach((el: Element) => {
      const mathml = el.getAttribute('data-mathml');
      if (mathml && !el.innerHTML.trim()) {
        el.innerHTML = mathml;
      }
    });
  }

  /**
 * Walks a cloned document fragment and reconstructs the content as an HTML
 * string, replacing MathJax SVG containers with sabis-equation-wrapper
 * structures so they can be inserted and rendered in the input component.
 * 
 * Handles both:
 * 1. Text selections that include equations (via cloneContents)
 * 2. Pure SVG/element selections where cloneContents is empty (via fallback)
 */
  public extractSelectionWithEquations(
    range: Range,
    messageContentRef?: ElementRef<HTMLElement>
  ): string {
    // Build a list of replacement HTML strings for every mjx-container that
    // intersects the selection, in live-DOM order (same order as cloned nodes).
    const equationHtmlList: string[] = [];
    const intersectingContainers: Element[] = [];
    const scope = messageContentRef?.nativeElement;
    if (!scope) return '';

    const allContainers = Array.from(
      scope.querySelectorAll(
        'mjx-container, .custom-content mjx-container, .math-larger mjx-container'
      )
    );
    for (const container of allContainers) {
      if (range.intersectsNode(container)) {
        intersectingContainers.push(container);
        equationHtmlList.push(this.resolveEquationHtml(container, messageContentRef));
      }
    }


    let equationIndex = 0;

    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        // HTML-escape plain text so it is safe inside the resulting HTML string
        return (node.textContent ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }
      if (node instanceof Element) {
        const tag = node.tagName.toLowerCase();
        if (tag === 'script' || tag === 'style') return '';
        if (tag === 'mjx-container') {
          return equationHtmlList[equationIndex++] ?? '';
        }
      }
      let result = '';
      node.childNodes.forEach(child => (result += processNode(child)));
      return result;
    };

    const clonedContent = range.cloneContents();
    let result = processNode(clonedContent).trim();

    // Fallback: if cloneContents was empty but we found intersecting containers,
    // use their resolved HTML directly (handles pure SVG/element selection where
    // the range spans an mjx-container but cloneContents() returns an empty fragment)
    if (!result && intersectingContainers.length > 0) {
      result = intersectingContainers
        .map((_, idx) => equationHtmlList[idx] ?? '')
        .filter(Boolean)
        .join('');
    }

    return result;
  }

  /**
   * For a live mjx-container element, resolves the MathML via MathJax tex2mml
   * and returns a sabis-equation-wrapper HTML string ready to insert into the input.
   */
  public resolveEquationHtml(
    container: Element,
    messageContentRef?: ElementRef<HTMLElement>
  ): string {
    const mj = (window as any).MathJax;
    const contextNode = messageContentRef?.nativeElement;
    const items: any[] = mj.startup.document.getMathItemsWithin(contextNode);
    const mjItem = items.find(
      (it: any) => it?.typesetRoot === container || it?.start?.node === container
    );

    let tex: string = (mjItem?.math as string) ?? '';
    const display: boolean = !!mjItem?.display;

    // TeX fallback from SVG data-latex attribute
    if (!tex) {
      tex = container.querySelector('g[data-mml-node="math"][data-latex]')?.getAttribute('data-latex') ?? '';
    }

    if (!tex) return '';

    const result: string = mj.tex2mml(tex, { display });
    const mathml = this.sanitizeMathML(result);
    return mathml?.includes('<math') ? this.buildEquationHtml(mathml) : '';
  }

  /**
   * Builds the sabis-equation-wrapper HTML string for a given MathML (or TeX) source.
   * sabis-equation inner content is intentionally empty — appendTextToInput calls
   * restoreEquationMathML which fills it from data-mathml before typesetting.
   */
  public buildEquationHtml(mathml: string): string {
    // Remove formatting whitespace in MathML
    const compactMathml = mathml
      .replace(/>\s+</g, '><')   // remove spaces/newlines between tags
      .replace(/\s{2,}/g, ' ')   // collapse repeated spaces
      .trim();
    // Encode for use as an HTML attribute value
    const encoded = compactMathml
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    return `<sabis-equation-wrapper contenteditable="false" style="display: inline-block;">` +
      `<sabis-equation contenteditable="false" data-mathml="${encoded}"></sabis-equation>` +
      `\u200B</sabis-equation-wrapper>`;
  }
}